// 简体中文 catalog. Typed as `Strings` (= typeof en) so it must stay in sync
// with en.ts — a missing or mistyped key fails the build.

import type { Strings } from "./index";

export const zh: Strings = {
  lang: {
    label: "语言",
    en: "English",
    zh: "中文",
  },

  units: {
    oz: "盎司",
    ml: "毫升",
  },

  common: {
    close: "关闭",
    cancel: "取消",
    save: "保存",
    delete: "删除",
    loading: "加载中…",
    somethingWrong: "出了点问题。",
    tryAgain: "重试",
  },

  pwa: {
    updateReady: "新版本已就绪。",
    reload: "刷新",
    later: "稍后",
    offline: "已离线——你的记录会先存在本地，联网后自动同步。",
    pendingSync: (n: number) => `${n} 条记录待同步`,
  },

  login: {
    title: "宝宝助手",
    subtitle: "登录以设置宝宝的记录。",
    google: "使用 Google 登录",
    googleSubmitting: "正在打开 Google…",
    or: "或",
    emailLabel: "你的邮箱",
    emailPlaceholder: "you@example.com",
    emailHint: "我们会给你发送一个 6 位验证码——无需记住密码。",
    submit: "给我发送验证码",
    submitting: "发送中…",
    sentTitle: "查收你的邮箱",
    sentBody: (email: string) =>
      `我们已将一个 6 位登录验证码发送至 ${email}。请在下方输入以完成登录。`,
    codeLabel: "6 位验证码",
    codePlaceholder: "123456",
    codeHint: "输入验证码即可在这台设备上保持登录——即使邮件链接在浏览器里打开也没关系。",
    verify: "验证并登录",
    verifying: "验证中…",
    codeError: "验证码不对——请检查邮件，或重新获取一个。",
    resend: "重新发送验证码",
  },

  createBaby: {
    title: "宝宝助手",
    subtitle: "和每一位帮手一起，记录喂养、睡眠和体重。",
    nameLabel: "宝宝的名字",
    namePlaceholder: "例如：依依",
    birthdayLabel: "出生日期",
    birthdayHint: "如果宝宝是早产儿，你可以稍后设置预产期。",
    yourNameLabel: "你的名字",
    yourNamePlaceholder: "例如：妈妈",
    yourNameHint: "其他帮手会看到的称呼。",
    submit: "创建",
    submitting: "创建中…",
    storageError: "存储尚未配置。请让开发者设置 Supabase 环境变量。",
  },

  join: {
    title: "加入记录",
    subtitle: "你受邀帮忙记录喂养、睡眠和尿布。",
    google: "使用 Google 登录",
    googleSubmitting: "正在打开 Google…",
    googleHint: "推荐——安装应用后也能保持登录。",
    anonAlt: "或不登录直接加入",
    anonHint: "快捷加入——但换设备后需要新的邀请链接。",
    nameLabel: "你的名字",
    namePlaceholder: "例如：阿姨",
    nameHint: "让家人知道是谁记录的。",
    submit: "加入",
    submitting: "加入中…",
    sessionError: "无法开始会话。请重试。",
    expired: "此邀请链接已过期——请索取新的链接。",
    invalid: "此邀请链接无效。",
    joinFailed: "无法加入。请稍后重试。",
  },

  babyShell: {
    noAccessTitle: "无访问权限",
    noAccessBody:
      "你没有此记录的访问权限。请向家人索取新的邀请链接，或创建自己的记录。",
    startNew: "创建新的记录",
    errorTitle: "嗯……",
    startOver: "重新开始",
    storageError: "存储尚未配置。",
  },

  babyPicker: {
    title: "选择宝宝",
    subtitle: "你是多个记录的看护人。点击你要打开的那个。",
    addBaby: "+ 添加另一个宝宝",
    noEntries: "还没有记录",
    never: "从未",
    activity: (count: number, when: string) => `${count} 条记录 · 最近记录于 ${when}`,
    minutesAgo: (n: number) => (n < 1 ? "刚刚" : `${n} 分钟前`),
    hoursAgo: (n: number) => `${n} 小时前`,
    daysAgo: (n: number) => `${n} 天前`,
  },

  menu: {
    share: "分享",
    weight: "体重",
    history: "历史",
    settings: "设置",
    switchBaby: "切换宝宝",
  },

  settings: {
    accountSection: "账户",
    signOut: "退出登录",
    signingOut: "正在退出…",
    signOutFailed: "无法退出登录，请重试。",

    deleteFailed: (msg: string) => `无法删除：${msg}`,
    back: "返回",
    title: "设置",
    babySection: "宝宝",
    photo: "照片",
    name: "名字",
    babyNamePlaceholder: "宝宝的名字",
    birthday: "出生日期",
    dueDate: "预产期",
    feedingSection: "喂养",
    bottleDefaultLabel: (unit: string) => `默认瓶喂量（${unit}）`,
    nurseDefaultLabel: (unit: string) => `默认亲喂量（${unit}）`,
    notSet: "未设置",
    feedingHelper: "快捷按钮会按这些默认值记录，所有帮手通用。留空则沿用上一次记录的量。",
    youSection: "你",
    yourNamePlaceholder: "你的名字",
    save: "保存",
    saved: "已保存",
    youAreCreator: (name: string) =>
      `你是 ${name} 记录的创建者。这个名字会让其他人知道是谁记录的。`,
    youAreCaretaker: (name: string) =>
      `你是 ${name} 记录的一位帮手。这个名字会让其他人知道是谁记录的。`,
    caretakersSection: "帮手",
    loading: "加载中…",
    youTag: "· 你",
    roleCreator: "创建者",
    roleCaretaker: "帮手",
    remove: "移除",
    sharingSection: "共享",
    inviteCaretaker: "邀请一位帮手",
    inviteHelper: "把邀请链接发给你的伴侣或阿姨，他们也能一起记录。",
    displaySection: "显示",
    milkUnits: "奶量单位",
    theme: "主题",
    dangerSection: "危险操作",
    deleteBaby: "删除这个宝宝",
    leaveTracker: "退出这个记录",
    leaveArmed: "再点一次确认退出",
    aboutSection: "关于",
    version: "版本",
  },

  feedSheet: {
    title: "喂奶",
    howMuch: "喝了多少？",
    customAmount: (unit: string) => `自定义奶量（${unit}）`,
    logFeed: (amt: string) => `记录这次喝了 ${amt}`,
  },

  diaperSheet: {
    title: "换尿布",
    wet: "尿湿",
    dirty: "便便",
    both: "又尿又便",
  },

  pumpSheet: {
    title: "吸奶",
    pumpedLabel: (unit: string) => `吸出奶量（${unit}）`,
    durationLabel: "用时（分钟，可不填）",
    logPump: "记录这次吸奶",
  },

  noteSheet: {
    save: "保存",
  },

  weightSheetSub: {
    title: "体重",
    caption: "是体检时量的吗？",
    pounds: "磅",
    ounces: "盎司",
    approx: (grams: number, lb: number, oz: number) => `约 ${grams} 克 · ${lb} 磅 ${oz} 盎司`,
    logWeight: "记录体重",
  },

  weightEntry: {
    title: "记录新体重",
    lb: "磅",
    oz: "盎司",
    lbOz: (lb: number, oz: number) => `${lb} 磅 ${oz} 盎司`,
    approxKg: (kg: string) => `约 ${kg} 公斤`,
    cancel: "取消",
    save: "保存",
  },

  weightSheet: {
    weightLabel: "体重",
    loggedRel: (rel: string) => `${rel}量的。`,
    noneYet: "还没有记录过体重。",
    wasBefore: (weight: string, rel: string) => ` ${rel}是 ${weight}。`,
    recentLabel: "最近几次称重",
    close: "关闭",
    logNew: "记录新体重",
    sparklineEmpty: "记录一次体重，就能看到变化趋势啦。",
    insightEmpty: (babyName: string) =>
      `记录一次体重，就能看到专门针对${babyName}的成长和夜间睡眠建议。`,
    insightBuilding: (lb: number, babyName: string) =>
      `现在 ${lb} 磅，${babyName}还在长身体。白天每隔 2–3 小时喂一次依然很重要，夜里拉长间隔是之后的事。`,
    insightOnTrack: (lb: number, babyName: string, hours: number) =>
      `现在 ${lb} 磅，${babyName}快可以开始一段较长的夜觉了。可以试着以大约 ${hours} 小时为目标。`,
    insightConsolidating: (lb: number, babyName: string) =>
      `现在 ${lb} 磅，${babyName}的体重已经很适合把夜觉睡得更整段了。我们可以只保留一次夜奶，喂到大约凌晨 4 点，不用再喂两次。`,
    lbOz: (lb: number, oz: number) => `${lb} 磅 ${oz} 盎司`,
    relToday: "今天",
    relYesterday: "昨天",
    relDaysAgo: (days: number) => `${days} 天前`,
  },

  deleteConfirm: {
    title: (babyName: string) => `要删除${babyName}的记录吗？`,
    body: "所有记录的喂奶、小睡、体重和笔记都会被永久删除，分享链接也会失效。删除后无法恢复。",
    cancel: "取消",
    confirm: "确认，全部删除。",
  },

  babyAvatar: {
    addPhoto: "添加照片",
    changePhoto: "更换照片",
    avatarLabel: (name: string) => `${name}的头像`,
  },

  composer: {
    chipWokeUp: "醒啦",
    chipNursing: (amt: string) => `亲喂 ${amt}`,
    chipJustFed: (amt: string) => `刚喂了 ${amt}`,
    chipSleepingNow: "正在睡",
    inputPlaceholder: "打字记一笔——比如“凌晨 4 点喂了 90 毫升”",
    openLogAria: "打开记录面板",
    errParseRateLimited: "今天的 AI 次数用完啦，可以点下方的 + 手动记录。",
    errParseUnavailable: "AI 识别暂时离线了，请点 + 手动记录。",
    errParseFailed: "没看懂这条记录，再试一次或点 + 手动记录。",
    confirmNeedMore: "还差一点信息",
    confirmHeard: "我听到的是……",
    confirmNothing: "没有需要记录的内容。",
    confirmCancel: "取消",
    confirmLooksRight: "没错，就这样",
    draftMethodBottle: "瓶喂",
    draftMethodNursed: "亲喂",
    draftOz: (amt: string) => amt,
    draftMin: (min: number) => `${min} 分钟`,
    draftBothSides: "两边",
    draftSideLeft: "左边",
    draftSideRight: "右边",
    draftNow: "现在",
    draftFeed: (summary: string, time: string) => `${summary}，${time}`,
    draftSleepLive: (time: string) => `${time} 开始入睡`,
    draftSlept: (start: string, end: string) => `睡了 ${start}–${end}`,
    draftWoke: (time: string) => `${time} 醒来`,
    draftDiaper: (kind: string, time: string) => `尿布：${kind}，${time}`,
    draftWeight: (lb: number, oz: number, time: string) => `${lb} 磅 ${oz} 盎司，${time}`,
  },

  logSheet: {
    addNap: "补记小睡",
    addNapSub: "选择开始和结束时间",
    bottle: "瓶喂",
    bottleSub: (amt: string) => `${amt} · 现在`,
    breast: "亲喂",
    breastSub: (amt: string) => `${amt} · 现在`,
    endNap: "结束小睡",
    endNapSub: "把她叫醒",
    startNap: "开始小睡",
    startNapSub: "实时计时",
    diaper: "尿布",
    diaperSub: "尿湿 · 便便",
    weight: "体重",
    weightSub: "磅 · 盎司",
    note: "备注",
    noteSub: "随手记点什么",
    cancel: "取消",
  },

  editEvent: {
    titleFeed: "修改喂养记录",
    titleNurse: "修改亲喂记录",
    titleSleep: "修改睡眠记录",
    titleDiaper: "修改尿布记录",
    titleEntry: "修改记录",
    fieldTime: "时间",
    fieldStarted: "开始时间",
    fieldEnded: "结束时间",
    endedHint: "如果还在睡，就先空着",
    fieldAmount: (unit: string) => `奶量（${unit}）`,
    fieldDuration: "时长（分钟）",
    fieldSide: "哪一边",
    sideLeft: "左边",
    sideRight: "右边",
    sideBoth: "两边",
    fieldKind: "类型",
    kindWet: "尿湿",
    kindDirty: "便便",
    kindBoth: "都有",
    cancel: "取消",
    save: "保存修改",
    deleteIdle: "删除这条记录",
    deleteArmed: "再点一次确认删除",
  },

  // 补记面板——往之前的日子里补一条漏记的记录。
  addEntry: {
    saveError: "未能保存，请重试。",
    fieldDate: "日期",
    title: "补记一条",
    addButton: "补记一条",
    fieldType: "记什么？",
    typeBottle: "瓶喂",
    typeNursing: "亲喂",
    typeSleep: "睡眠",
    typeDiaper: "尿布",
    typeNote: "备注",
    endRequiredHint: "什么时候结束的？补记睡眠需要填结束时间。",
    notePlaceholder: "随手记点什么",
    save: "添加记录",
  },

  header: {
    todayDate: (date: string) => `今天，${date}`,
    adjustedSuffix: "（矫正）",
    prevDay: "前一天",
    nextDay: "后一天",
    menu: "菜单",
    aboutBaby: (name: string) => `了解 ${name} —— 当前阶段和接下来会发生什么`,
    addWeight: "记录体重",
    weightValue: (lb: number, oz: number) => `${lb} 磅 ${oz} 盎司`,
  },

  trackCards: {
    milk: "喝奶",
    sleep: "睡觉",
    ozAmount: (amt: string) => amt,
    feedsToday: (count: number) => `今天已喂 ${count} 次`,
    napsToday: (count: number) => `今天已睡 ${count} 觉`,
    nextFeed: "下次喝奶",
    nextNap: "下次小睡",
    nextBedtime: "今晚就寝",
    tryRange: (range: string) => `可以试试 ${range}`,
    tryAmount: (amt: string) => `可以试试 ${amt}`,
    feedOnCues: "看她的需求来喂",
    feedOnHerCues: "看她的需求来喂",
    feedGuideline: (loHrs: number, hiHrs: number) => `大约每 ${loHrs}–${hiHrs} 小时一次`,
    wakeGuideline: (loMin: number, hiMin: number) => `清醒 ${loMin}–${hiMin} 分钟左右`,
    sleepingNow: "正在睡觉",
    since: (time: string) => `从 ${time} 开始`,
    tapToEnd: "点一下结束",
    tapToStart: "点一下开始",
    cancel: "取消",
    confirmFeedTitle: (name: string) => `现在记录给 ${name} 喂奶吗？`,
    confirmFeedBody: "会立刻记一次喂奶 —— 具体的奶量稍后可以在时间线里慢慢调整。",
    confirmFeedCta: "好的，开始喂奶",
    confirmStartNapTitle: (name: string) => `现在记录 ${name} 开始小睡吗？`,
    confirmStartNapBody: "会从现在开始计时睡觉。",
    confirmStartNapCta: "好的，她睡了",
    confirmEndNapTitle: (name: string) => `结束 ${name} 这次小睡吗？`,
    confirmEndNapBody: "会把当前这次小睡记为现在结束。",
    confirmEndNapCta: "好的，她醒了",
  },

  dayRibbon: {
    today: "今天",
    span: (start: string, end: string, hours: number) => `${start} → ${end} · 共 ${hours} 小时`,
  },

  dayTimeline: {
    today: "今天",
    history: "历史",
    now: "现在",
    empty: "今天还没有任何记录。",
    titleFeed: "待喂奶",
    titleFed: "喝奶",
    titleNursed: "亲喂",
    titleNap: "小睡",
    titleSleeping: "正在睡",
    titleNightSleep: "夜间睡眠",
    titleBedtime: "就寝",
    titleDiaper: "换尿布",
    detailRangeOz: (range: string) => `约 ${range}`,
    detailApproxOz: (amt: string) => `约 ${amt}`,
    detailPredicted: "预计",
    detailNursed: (min: number, amt: string) => `${min} 分钟 · 约 ${amt}`,
    detailOz: (amt: string) => amt,
    detailMin: (min: number) => `${min} 分钟`,
    detailInProgress: "进行中",
    detailApproxMin: (min: number) => `约 ${min} 分钟`,
    detailBedtimeRoutine: "睡前流程约提前 30 分钟",
    diaperBoth: "又湿又脏",
  },

  pastDay: {
    dash: "—",
    intake: "总奶量",
    daytimeSleep: "白天睡眠",
    longestStretch: "最长连续睡眠",
    ozValue: (amt: string) => amt,
    events: "当天记录",
    empty: "这一天没有任何记录。",
    labelBottle: "瓶喂",
    labelNursed: "亲喂",
    labelNightSleep: "夜间睡眠",
    labelNap: "小睡",
    labelWake: "醒来",
    labelBedtime: "就寝",
    detailOz: (amt: string) => amt,
    detailMin: (min: number) => `${min} 分钟`,
    thatNight: "那一晚",
    nightSummary: (feeds: number) => ` 最长连续睡眠 · 夜间喂奶 ${feeds} 次`,
    feedsAt: (times: string) => ` · 在 ${times}`,
    weight: "体重",
    weighedToday: (lb: number, oz: number) => `今天称重 ${lb} 磅 ${oz} 盎司。`,
  },

  guidance: {
    heading: "今日指引",
    wakeWindow: "清醒时长",
    asleepNow: "正在睡觉",
    noNapsYet: "还没有记录小睡",
    noRecentNap: "最近还没小睡",
    minsAwake: (mins: number) => `已清醒 ${mins} 分钟`,
    feeds: "喂养",
    feedEvery: (min: number, max: number) => `每 ${min}–${max} 小时一次`,
    feedsSub: "目前白天和夜里都一样",
    daySleep: "白天睡眠",
    pastGuide: "超过建议时长了",
    withinGuide: "在建议范围内",
    noCapYet: "暂无上限",
    longestGap: "最长间隔",
    gapSpan: (from: string, to: string) => `${from} → ${to}`,
    noGapYet: "喂奶记录还不够",
  },

  driftBanner: {
    dismiss: "知道了",
  },

  stage: {
    about: "关于宝宝",
    age: "月龄",
    weight: "体重",
    stage: "阶段",
    wakeWindow: "清醒时长",
    whatsHappening: "现在发生着什么",
    whatToFocus: "现在可以多留意",
    close: "关闭",
  },

  share: {
    title: "邀请一位帮手",
    body: (name: string) =>
      `打开这个链接的人，都可以帮忙记录 ${name} 的喂奶、睡眠和换尿布。只有你能修改 ${name} 的资料或移除帮手。链接 30 天内有效。`,
    preparing: "正在生成邀请链接…",
    creatorOnly: "只有宝宝的创建者才能生成邀请链接。",
    shareEllipsis: "分享…",
    close: "关闭",
    copied: "已复制！",
    copyLink: "复制链接",
    shareTitle: (name: string) => `${name} · 宝宝助手`,
    shareText: (name: string) => `一起来帮忙记录 ${name} 吧`,
  },

  history: {
    back: "返回",
    title: "本周",
    avgIntake: "平均奶量",
    avgDaytimeSleep: "白天平均睡眠",
    avgLongestStretch: "平均最长连睡",
    dayByDay: "每日记录",
    today: "今天",
    yesterday: "昨天",
    noSleep: "— 睡眠",
    overnight: (dur: string) => `夜间连睡 ${dur}`,
    noOvernight: "— 夜间连睡",
    whatWeNoticed: "我们注意到",
    obsStretchLengthened: (fromHrs: number, toHrs: number) =>
      `夜间连睡变长了。过去一周里，从大约 ${fromHrs} 小时延长到了大约 ${toHrs} 小时。`,
  },

  // Adaptive coaching hints from the schedule engine (schedule.ts).
  hints: {
    daySleepTitle: "白天睡得有点多",
    daySleepDetail: (hrs: number, capHrs: number) =>
      `目前白天已经睡了大约 ${hrs} 小时——超过了这个月龄约 ${capHrs} 小时的建议。白天小睡太久，会借走夜里的睡眠。`,
    wakeWindowExceededTitle: "已超过清醒时长",
    wakeWindowExceededDetail: (awakeMin: number, maxMin: number) =>
      `已清醒 ${awakeMin} 分钟——这个月龄通常 ${maxMin} 分钟左右就该小睡了。`,
    wakeWindowNearTitle: "快到小睡时间了",
    wakeWindowNearDetail: (awakeMin: number, minMin: number, maxMin: number) =>
      `已清醒 ${awakeMin} 分钟——清醒时长大约是 ${minMin}–${maxMin} 分钟。`,
    feedOverdueHardTitle: "该喂奶了",
    feedOverdueHardDetail: (sinceHrs: string, maxHrs: number) =>
      `距离上次喂奶已经 ${sinceHrs} 小时——白天两次喂奶之间最好不要超过 ${maxHrs} 小时。`,
    feedOverduePersonalTitle: "差不多该喂了（按平时节奏）",
    feedOverduePersonalDetail: (sinceHrs: string, typicalHrs: string) =>
      `上次喂奶是 ${sinceHrs} 小时前——平时这个时候 ${typicalHrs} 小时左右你就会喂了。`,
    clusterTitle: "到了密集喂养时段",
    clusterDetail: "睡前这段时间，喂奶间隔短一些是正常的——这是“储奶”阶段。",
    catnapTitle: "可以考虑来个小小睡",
    catnapDetail: (sinceMin: number, bedtime: string) =>
      `上次小睡在 ${sinceMin} 分钟前结束——在 ${bedtime} 就寝前来一个 15–45 分钟的小小睡，可以撑到睡觉时间。`,
    reverseCyclingTitle: "大部分喂奶在夜间",
    reverseCyclingDetail: (nightPct: number) =>
      `过去 7 天里，${nightPct}% 的喂奶次数（瓶喂和亲喂）发生在夜间。这就是“昼夜颠倒”——试着白天多喂几次、喂足。`,
    nightStretchBehindTitle: "夜间连睡保持平稳",
    nightStretchBehindDetail: (medHrs: number, targetHrs: number) =>
      `最近 7 晚平均连睡约 ${medHrs} 小时，还没到这个月龄 ${targetHrs} 小时的目标。保持就寝时间稳定就好——不用勉强。`,
    rabTitle: "这次亲喂时间较长",
    rabDetail:
      "留意宝宝是否在喂奶时打瞌睡（含着乳房休息）。如果中间睡了一小觉，可能会消耗掉下一次小睡的睡眠驱动力。",
    regressionTitle: "夜间睡眠有所下降",
    regressionDetailReframe: (priorHrs: number, recentHrs: number) =>
      `夜间睡眠从大约 ${priorHrs} 小时降到了 ${recentHrs} 小时——这是 4 个月左右的睡眠重组期。这很正常，会过去的；保持就寝时间稳定。`,
    regressionDetailPlain: (priorHrs: number, recentHrs: number) =>
      `最近这几晚，夜间睡眠从大约 ${priorHrs} 小时降到了 ${recentHrs} 小时。保持作息稳定——通常会慢慢恢复。`,
  },

  // Human label for each developmental phase (band.phase → display string).
  phaseLabel: {
    "supply-building": "建立泌乳阶段",
    foundation: "打基础阶段",
    stretching: "拉长夜觉阶段",
    "regression-prep": "3–4 月龄过渡期",
    "regression-active": "4 月龄活跃期",
    "pre-sleep-training": "准备睡整夜",
  },
};
